/**
 * Tab: Conversation Review (F19)
 * Auditor browse of stored conversations. List only — the stats summary (M36)
 * and the detail pane (M35) land in later slices.
 */

import React, { useState, useEffect } from 'react';
import { LoadingPanel, ErrorPanel } from '../shared/LoadingPanel';
import { useConversations, useConversationStats, formatRelativeTime } from '../hooks/useAdminData';
import { getConversationDetail } from '../../../api/adminApi';
import type { ConversationDetail } from '../../../types/admin';

export const ConversationReview: React.FC = () => {
  const [offset, setOffset] = useState(0);
  const limit = 50;
  // Selected conversation drives the detail pane below. The list rows carry no
  // messages, so selection triggers a separate detail fetch (M35).
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  // retryTick lets the ErrorPanel re-trigger the fetch for the SAME chat_id
  // (re-selecting the same id wouldn't change the effect dep on its own).
  const [retryTick, setRetryTick] = useState(0);

  const { data, loading, error, lastRefreshed, refresh } = useConversations({ limit, offset });
  const stats = useConversationStats(30, 120_000);

  // Imperative detail fetch — deliberately NOT a polling hook: polling a
  // conversation an auditor is mid-read is wrong, and usePolling has no
  // null-skip. The cleanup ignore-flag discards a superseded fetch so fast
  // row-clicks can't clobber a later selection with an earlier response.
  useEffect(() => {
    if (!selectedChatId) {
      setDetail(null);
      setDetailError(null);
      return;
    }
    let ignore = false;
    setDetailLoading(true);
    setDetailError(null);
    getConversationDetail(selectedChatId)
      .then((d) => { if (!ignore) setDetail(d); })
      .catch((e) => {
        if (!ignore) setDetailError(e instanceof Error ? e.message : 'Failed to load conversation');
      })
      .finally(() => { if (!ignore) setDetailLoading(false); });
    return () => { ignore = true; };
  }, [selectedChatId, retryTick]);

  return (
    <div className="adm-tab-content">
      <p className="adm-muted" style={{ marginBottom: 12, fontSize: '0.8rem' }}>
        This panel shows real user conversations for audit and governance purposes.
      </p>
      <div className="adm-tab-header">
        <h2 className="adm-tab-title">Conversation Review</h2>
        <span className="adm-refresh-label">
          Updated {formatRelativeTime(lastRefreshed)}
        </span>
      </div>

      {/* Stats summary — own tri-state on useConversationStats, independent of the
          list block below. Windowed totals + lifetime feedback + verbatim notes. */}
      {stats.loading && !stats.data ? (
        <LoadingPanel message="Loading stats…" />
      ) : stats.error && !stats.data ? (
        <ErrorPanel message={`Failed to load stats (${stats.error})`} onRetry={stats.refresh} />
      ) : stats.data ? (
        <div style={{ marginBottom: 20 }}>
          {/* Windowed totals — last period.days days. Fields are always present
              per the stats contract, so NO ?? fallback: 0 is a real value. */}
          <div className="adm-kpi-row">
            <div className="adm-kpi-card">
              <div className="adm-kpi-value adm-mono">{stats.data.totals.conversations.toLocaleString()}</div>
              <div className="adm-kpi-label">Conversations</div>
            </div>
            <div className="adm-kpi-card">
              <div className="adm-kpi-value adm-mono">{stats.data.totals.messages.toLocaleString()}</div>
              <div className="adm-kpi-label">Messages</div>
            </div>
            <div className="adm-kpi-card">
              <div className="adm-kpi-value adm-mono">{stats.data.totals.unique_users.toLocaleString()}</div>
              <div className="adm-kpi-label">Unique Users</div>
            </div>
            <div className="adm-kpi-card">
              {/* float, 2dp per type — toFixed(2), NOT toLocaleString (which drops precision) */}
              <div className="adm-kpi-value adm-mono">{stats.data.totals.avg_messages_per_conversation.toFixed(2)}</div>
              <div className="adm-kpi-label">Avg Msgs / Conversation</div>
            </div>
          </div>

          {/* Lifetime feedback — DIFFERENT grain from the windowed totals above.
              The visual seam (audit-grid vs kpi-row) reinforces window_note. */}
          <div className="adm-audit-grid" style={{ marginTop: 12 }}>
            <div className="adm-audit-stat">
              <span className="adm-audit-value adm-mono" style={{ color: '#28A745' }}>{stats.data.feedback.thumbs_up.toLocaleString()}</span>
              <span className="adm-audit-label">Thumbs Up</span>
            </div>
            <div className="adm-audit-stat">
              <span className="adm-audit-value adm-mono" style={{ color: '#DC3545' }}>{stats.data.feedback.thumbs_down.toLocaleString()}</span>
              <span className="adm-audit-label">Thumbs Down</span>
            </div>
          </div>

          {/* Governance disclosures — rendered VERBATIM from the payload, never
              paraphrased, never dismissible. */}
          <p className="adm-muted" style={{ marginTop: 12, fontSize: '0.8rem' }}>{stats.data.window_note}</p>
          <p className="adm-muted" style={{ marginTop: 4, fontSize: '0.8rem' }}>{stats.data.source_note}</p>

          {/* Stats-specific truncation band — amber role="status" STYLE reused from the
              list banner, but copy is about aggregate UNDERCOUNT, not a display cutoff. */}
          {stats.data.truncated ? (
            <div role="status" style={{ marginTop: 12, padding: '10px 14px',
              background: 'rgba(255,193,7,0.1)', border: '1px solid rgba(255,193,7,0.4)',
              borderRadius: 4, fontSize: '0.8rem', color: '#212529' }}>
              These totals are computed over a capped scan (first {stats.data.scan_cap.toLocaleString()} rows) and may undercount.
            </div>
          ) : null}
        </div>
      ) : null}

      {loading && !data ? (
        <LoadingPanel message="Loading conversations..." />
      ) : error && !data ? (
        <ErrorPanel message={`Failed to load conversations (${error})`} onRetry={refresh} />
      ) : data ? (
        <>
          {/* Honesty banner: the header scan is capped, so a full result set
              may be incomplete. Factual about the cap — no filter language
              (filters don't exist yet). */}
          {data.truncated && (
            <div
              role="status"
              style={{
                marginBottom: 12,
                padding: '10px 14px',
                background: 'rgba(255,193,7,0.1)',
                border: '1px solid rgba(255,193,7,0.4)',
                borderRadius: 4,
                fontSize: '0.8rem',
                color: '#212529',
              }}
            >
              Showing the most recent {data.total.toLocaleString()} of a capped scan
              (first {data.scan_cap.toLocaleString()} rows). Older conversations exist
              beyond the cap.
            </div>
          )}

          {data.conversations.length === 0 ? (
            // Empty state, NOT a bare empty tbody — a failed scan is caught by the
            // error arm above, so reaching here means the scan genuinely found none.
            <p className="adm-muted">No conversations found.</p>
          ) : (
            <>
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Updated</th>
                    <th>User</th>
                    <th>Title</th>
                    <th>Messages</th>
                  </tr>
                </thead>
                <tbody>
                  {data.conversations.map((c) => (
                    <tr
                      key={c.chat_id}
                      onClick={() => setSelectedChatId(c.chat_id)}
                      style={{
                        cursor: 'pointer',
                        // No adm-row-selected class exists in AdminDashboard.css,
                        // so the selected tint is inline rather than a phantom class.
                        background: c.chat_id === selectedChatId ? 'rgba(0,86,179,0.08)' : undefined,
                      }}
                    >
                      <td className="adm-mono" style={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                        {c.updated_at ? new Date(c.updated_at).toLocaleDateString() : '—'}
                      </td>
                      <td>{c.user_name ?? c.user_email ?? '—'}</td>
                      <td style={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.title.trim() ? c.title : 'Untitled'}
                      </td>
                      <td className="adm-mono">{c.message_count.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {data.total > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
                  <button
                    className="adm-btn adm-btn-sm"
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                  >
                    Prev
                  </button>
                  <button
                    className="adm-btn adm-btn-sm"
                    disabled={offset + limit >= data.total}
                    onClick={() => setOffset(offset + limit)}
                  >
                    Next
                  </button>
                  <span className="adm-mono" style={{ fontSize: '0.75rem', color: '#495057' }}>
                    {offset + 1}–{Math.min(offset + limit, data.total)} of {data.total.toLocaleString()}
                  </span>
                </div>
              )}
            </>
          )}

          {/* Detail pane (M35) — one card below the list, shown when a row is
              selected. Pane-scoped tri-state; independent of the list's own
              loading/error above. */}
          {selectedChatId && (
            <div className="adm-card" style={{ marginTop: 16 }}>
              {detailLoading ? (
                <LoadingPanel message="Loading conversation…" />
              ) : detailError ? (
                <ErrorPanel message={detailError} onRetry={() => setRetryTick((t) => t + 1)} />
              ) : detail ? (
                <>
                  <div className="adm-card-title">
                    {detail.title.trim() ? detail.title : 'Untitled'}
                  </div>
                  <div className="adm-muted" style={{ marginBottom: 12, fontSize: '0.78rem' }}>
                    {detail.user_name ?? detail.user_email ?? '—'} ·{' '}
                    {detail.created_at ? new Date(detail.created_at).toLocaleString() : '—'}
                  </div>

                  {detail.messages.length === 0 ? (
                    // Distinct from the error arm above — a real zero-message conversation.
                    <p className="adm-muted">No messages in this conversation.</p>
                  ) : (
                    detail.messages.map((msg, i) => (
                      <div key={i} style={{ padding: '10px 0', borderBottom: '1px solid #E9ECEF' }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                          <span style={{ fontWeight: 600, fontSize: '0.8rem' }}>{msg.role}</span>
                          <span className="adm-mono" style={{ fontSize: '0.7rem', color: '#495057' }}>
                            {msg.timestamp ? new Date(msg.timestamp).toLocaleString() : ''}
                          </span>
                        </div>
                        <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>{msg.content}</div>

                        {/* Telemetry chips — presence-guarded ('field' in msg), NEVER
                            truthiness: refused===false and citation_count===0 are real. */}
                        {('refused' in msg || 'retrieval_rung' in msg || 'citation_count' in msg) && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6, alignItems: 'center', fontSize: '0.72rem', color: '#495057' }}>
                            {'refused' in msg && (
                              <span>refused: <span className="adm-badge-info">{String(msg.refused)}</span></span>
                            )}
                            {'retrieval_rung' in msg && (
                              <span>rung: <span className="adm-badge-info">{msg.retrieval_rung}</span></span>
                            )}
                            {'citation_count' in msg && (
                              <span>citations: <span className="adm-badge-info">{msg.citation_count}</span></span>
                            )}
                          </div>
                        )}

                        {'citations' in msg && msg.citations && msg.citations.length > 0 && (
                          <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: '0.75rem', color: '#495057' }}>
                            {msg.citations.map((cit, ci) => (
                              <li key={ci}>
                                <span className="adm-mono">[{cit.source_id}]</span> {cit.vendor} — “{cit.quote}”
                              </li>
                            ))}
                          </ul>
                        )}

                        {'sources' in msg && msg.sources && msg.sources.length > 0 && (
                          <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: '0.75rem', color: '#495057' }}>
                            {msg.sources.map((s, si) => (
                              <li key={si}>
                                {s.source !== '' ? (
                                  <a className="adm-link" href={s.source} target="_blank" rel="noopener noreferrer">
                                    {s.vendor_display || s.title || 'source'}
                                  </a>
                                ) : (
                                  <span>{s.vendor_display || s.title || 'source'}</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}

                        {'image' in msg && msg.image && (
                          <img
                            src={`data:image/png;base64,${msg.image}`}
                            alt="attached"
                            style={{ maxWidth: '100%', marginTop: 8, borderRadius: 4 }}
                          />
                        )}
                      </div>
                    ))
                  )}
                </>
              ) : null}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};
